import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useCallback } from "react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { contactFormSchema, ContactFormValues, departmentOptions } from "@/components/leads/add/schema";
import { JobTitleAutocomplete } from "@/components/ui/job-title-autocomplete";
import { DepartmentAutocomplete } from "@/components/ui/department-autocomplete";
import { DynamicUrlField } from "./DynamicUrlField";
import { DynamicMetadataField } from "./DynamicMetadataField";
import DynamicContactInfoField from "./DynamicContactInfoField";
import { LeadContact } from "@/types/lead";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, Edit } from "lucide-react";
import { trimPhoneNumber } from "@/utils/phoneNumberFormatter";

interface AddContactFormProps {
  onSubmit: (values: ContactFormValues) => void;
  isSubmitting: boolean;
  onCancel: () => void;
  departments?: string[];
  editContact?: any; // Contact data for edit mode
  selectedLead?: any; // Selected lead data
  onChangeLead?: () => void; // Function to change lead
}

export const AddContactForm = ({
  onSubmit,
  isSubmitting,
  onCancel,
  departments = [],
  editContact,
  selectedLead,
  onChangeLead
}: AddContactFormProps) => {
  const isEditMode = !!editContact;

  // Stable default values to prevent infinite re-renders
  const defaultValues = useMemo((): Partial<ContactFormValues> => {
    if (!isEditMode || !editContact) {
      return {
        first_name: "",
        last_name: "",
        designation: "",
        department: "",
        email: [],
        phone: [],
        decision_maker: false,
        
        notes: "",
        status: "Active",
        urls: {},
        metadata: {},
      };
    }

    console.log("🔍 AddContactForm: Raw editContact data:", editContact);
    console.log("🔍 AddContactForm: editContact.emails:", editContact.emails);
    console.log("🔍 AddContactForm: editContact.phones:", editContact.phones);

    return {
      first_name: editContact.first_name || "",
      last_name: editContact.last_name || "",
      designation: editContact.designation || "",
      department: editContact.department || "",
      email: (() => {
        // Handle both direct email arrays and nested structures
        if (Array.isArray(editContact.email)) {
          return editContact.email.filter(e => e.email?.trim()).map(e => ({
            email: e.email,
            type: e.type || 'official',
            is_primary: e.is_primary || false
          }));
        } else if (Array.isArray(editContact.emails)) {
          const emails = [];
          for (const emailItem of editContact.emails) {
            // Handle JSON string in address field
            if (typeof emailItem.address === 'string' && emailItem.address.startsWith('[')) {
              try {
                const parsed = JSON.parse(emailItem.address);
                if (Array.isArray(parsed)) {
                  emails.push(...parsed);
                } else {
                  emails.push(parsed);
                }
              } catch (e) {
                // Fallback for invalid JSON
                emails.push({ email: emailItem.address, type: 'official', is_primary: false });
              }
            } else if (typeof emailItem.address === 'string') {
              // Handle plain string emails (like metadata)
              emails.push({ email: emailItem.address, type: 'official', is_primary: emailItem.isPrimary || false });
            } else if (emailItem.email) {
              // Handle direct email objects
              emails.push(emailItem);
            }
          }
          return emails;
        } else if (typeof editContact.email === 'string' && editContact.email.trim()) {
          return [{ email: editContact.email, type: 'official', is_primary: true }];
        }
        return [];
      })(),
      phone: (() => {
        // Handle both direct phone arrays and nested structures
        if (Array.isArray(editContact.phone)) {
          return editContact.phone.filter(p => p.phone?.trim()).map(p => ({
            phone: p.phone,
            type: p.type || 'official',
            is_primary: p.is_primary || false
          }));
        } else if (Array.isArray(editContact.phones)) {
          const phones = [];
          for (const phoneItem of editContact.phones) {
            // Handle JSON string in number field
            if (typeof phoneItem.number === 'string' && phoneItem.number.startsWith('[')) {
              try {
                const parsed = JSON.parse(phoneItem.number);
                if (Array.isArray(parsed)) {
                  phones.push(...parsed);
                } else {
                  phones.push(parsed);
                }
              } catch (e) {
                // Fallback for invalid JSON
                phones.push({ phone: phoneItem.number, type: 'official', is_primary: false });
              }
            } else if (typeof phoneItem.number === 'string') {
              // Handle plain string phones (like metadata)
              phones.push({ phone: phoneItem.number, type: 'official', is_primary: phoneItem.isPrimary || false });
            } else if (phoneItem.phone) {
              // Handle direct phone objects
              phones.push(phoneItem);
            }
          }
          return phones;
        } else if (typeof editContact.phone === 'string' && editContact.phone.trim()) {
          return [{ phone: editContact.phone, type: 'official', is_primary: true }];
        } else if (typeof editContact.phone_number === 'string' && editContact.phone_number.trim()) {
          return [{ phone: editContact.phone_number, type: 'official', is_primary: true }];
        }
        return [];
      })(),
      decision_maker: Boolean(editContact.decision_maker || editContact.is_primary || editContact.is_decision_maker),
      
      notes: editContact.notes || "",
      status: editContact.status || editContact.contact_status || "Active",
      urls: (() => {
        try {
          const urls = editContact.urls;
          if (typeof urls === 'string') {
            return JSON.parse(urls);
          }
          return urls || {};
        } catch (error) {
          console.error('Failed to parse URLs in defaultValues:', error);
          return {};
        }
      })(),
      metadata: (() => {
        try {
          const metadata = editContact.metadata;
          if (typeof metadata === 'string') {
            return JSON.parse(metadata);
          }
          return metadata || {};
        } catch (error) {
          console.error('Failed to parse metadata in defaultValues:', error);
          return {};
        }
      })(),
    };
  }, [isEditMode, editContact?.id]);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues,
    mode: "onChange",
  });

  // Reset form when editContact changes (for edit mode)
  const resetForm = useCallback(() => {
    if (isEditMode && editContact) {
      console.log("🔄 AddContactForm: Resetting form with data:", defaultValues);
      // Clear all form errors first
      form.clearErrors();
      // Reset form with new values
      form.reset(defaultValues, { keepErrors: false });
      // Ensure decision_maker field is properly set
      form.setValue("decision_maker", defaultValues.decision_maker || false, { shouldValidate: false });
    }
  }, [editContact?.id, defaultValues, form]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const handleSubmit = (values: ContactFormValues) => {
    console.log("🚀 AddContactForm: FORM SUBMISSION TRIGGERED!");
    console.log("📝 AddContactForm: Form submission with values:", values);
    console.log("🔍 AddContactForm: Metadata being submitted:", values.metadata);
    console.log("🔍 AddContactForm: Form dirty state:", form.formState.isDirty);
    console.log("🔍 AddContactForm: Form dirty fields:", form.formState.dirtyFields);
    console.log("🔍 AddContactForm: Form valid state:", form.formState.isValid);
    console.log("🔍 AddContactForm: Form errors:", form.formState.errors);
    
    // Extract primary phone and email for backward compatibility
    const primaryPhone = Array.isArray(values.phone) && values.phone.length > 0 
      ? values.phone.find(p => p.is_primary)?.phone || values.phone[0]?.phone || ""
      : "";
    const primaryEmail = Array.isArray(values.email) && values.email.length > 0
      ? values.email.find(e => e.is_primary)?.email || values.email[0]?.email || ""
      : "";

    const finalValues = {
      ...values,
      // Keep both the JSON arrays and backward compatible strings
      phone_number: primaryPhone, // Legacy field
      email_legacy: primaryEmail, // Legacy field
    };

    console.log("📦 AddContactForm: Calling onSubmit with processed values:", finalValues);
    onSubmit(finalValues);
  };

  // Debug: Log form validation state changes
  useEffect(() => {
    const subscription = form.watch((values, { name, type }) => {
      if (type === 'change') {
        console.log(`🔄 AddContactForm: Field '${name}' changed, form valid: ${form.formState.isValid}`);
        if (Object.keys(form.formState.errors).length > 0) {
          console.log("❌ AddContactForm: Current form errors:", form.formState.errors);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="h-full flex flex-col">
        <div className="flex-1 overflow-y-auto scrollbar-custom px-4 py-3">
          <div className="space-y-3">
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="contact">Contact Info</TabsTrigger>
                <TabsTrigger value="social">Social & URLs</TabsTrigger>
                <TabsTrigger value="additional">Additional Info</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-2 mt-3">
                {/* Lead Card - Always shown in Basic tab when there's lead data or in edit mode */}
                {(selectedLead || (isEditMode && editContact)) && (
                  <Card className="border-2 border-primary/20 bg-primary/5">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between min-w-0">
                        <div className="flex items-center space-x-2 min-w-0 flex-1">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Building2 className="h-5 w-5 text-primary" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-medium truncate">
                              {selectedLead?.name || 
                               editContact?.company?.name || 
                               editContact?.company_name || 
                               'Unknown Company'}
                            </h3>
                            <p className="text-sm text-muted-foreground truncate">
                              {selectedLead?.industry || 
                               editContact?.industry || 
                               'Unknown Industry'}
                            </p>
                          </div>
                        </div>
                        {onChangeLead && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={onChangeLead}
                            className="h-8 w-8 p-0 flex-shrink-0 ml-2"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter first name" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter last name" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    control={form.control}
                    name="designation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Job Title</FormLabel>
                        <FormControl>
                          <JobTitleAutocomplete
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholder="Search or enter job title..."
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="department"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Department</FormLabel>
                        <FormControl>
                          <DepartmentAutocomplete
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholder="Search or enter department..."
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>


                <FormField
                  control={form.control}
                  name="decision_maker"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal">
                        Primary Contact (main decision maker)
                      </FormLabel>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              <TabsContent value="contact" className="space-y-2 mt-3">
                {/* Dynamic Contact Info Section */}
                <div className="space-y-2">
                  <DynamicContactInfoField
                    initialPhone={(form.watch('phone') || []) as any}
                    initialEmail={(form.watch('email') || []) as any}
                    onContactInfoChange={(data) => {
                      console.log("🔄 AddContactForm: onContactInfoChange called with:", data);
                      
                      // Always handle phone data (even if empty array)
                      if (data.phone !== undefined) {
                        // Filter out empty entries and ensure required properties
                        const cleanedPhone = data.phone.filter(p => p.phone?.trim() && p.type).map(p => ({
                          phone: p.phone!,
                          type: p.type!,
                          is_primary: p.is_primary || false
                        }));
                        console.log("📞 AddContactForm: Setting phone to:", cleanedPhone);
                        form.setValue("phone", cleanedPhone as any, { shouldValidate: true, shouldDirty: true });
                      }
                      
                      // Always handle email data (even if empty array)
                      if (data.email !== undefined) {
                        // Filter out empty entries and ensure required properties
                        const cleanedEmail = data.email.filter(e => e.email?.trim() && e.type).map(e => ({
                          email: e.email!,
                          type: e.type!,
                          is_primary: e.is_primary || false
                        }));
                        console.log("📧 AddContactForm: Setting email to:", cleanedEmail);
                        form.setValue("email", cleanedEmail as any, { shouldValidate: true, shouldDirty: true });
                      }
                    }}
                  />
                </div>
              </TabsContent>

              <TabsContent value="social" className="space-y-2 mt-3">
                <div className="space-y-2">
                  <FormLabel>Social & Professional URLs</FormLabel>
                  <div className="max-h-64 overflow-y-auto scrollbar-custom">
                    <DynamicUrlField
                      initialUrls={form.watch('urls') || {}}
                      onUrlsChange={(urls) => form.setValue("urls", urls)}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="additional" className="space-y-2 mt-3">
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Additional notes about this contact..."
                          className="min-h-[80px]"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-2">
                  <FormLabel>Additional Metadata</FormLabel>
                  <div className="max-h-64 overflow-y-auto scrollbar-custom">
                    <DynamicMetadataField
                      initialMetadata={form.watch('metadata') || {}}
                      onMetadataChange={(metadata) => {
                        console.log("📝 AddContactForm: Setting metadata field:", metadata);
                        form.setValue("metadata", metadata, { shouldValidate: true, shouldDirty: true });
                        // Watch for form state changes to debug
                        console.log("📊 AddContactForm: Current form metadata value:", form.getValues("metadata"));
                      }}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
        
        {/* Fixed Footer */}
        <div className="border-t bg-background px-4 py-3">
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : (isEditMode ? "Update Contact" : "Add Contact")}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default AddContactForm;
